import { useEffect, useState } from "react";
import { Check, Coins, Landmark, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtMYT } from "@/lib/myt";

const EMPTY = { bank_name: "", account_holder_name: "", bank_account_number: "", link: "" };
const RATE = 4.2;

const STATUS_STYLES = {
  pending: "text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/30",
  completed: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
};

export default function IBLocalDepositor() {
  const [data, setData] = useState(undefined); // undefined=loading, null=not active
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [requests, setRequests] = useState([]);
  const [approving, setApproving] = useState(null); // request id currently approving

  const loadWorkspace = () => {
    api.get("/ib/local-depositor")
      .then((res) => { setData(res.data); setForm({ ...EMPTY, ...(res.data.profile || {}) }); })
      .catch(() => setData(null));
    api.get("/ib/local-depositor/requests").then((res) => setRequests(res.data.requests || [])).catch(() => setRequests([]));
  };
  useEffect(loadWorkspace, []);

  const change = (f) => (e) => setForm((s) => ({ ...s, [f]: e.target.value }));

  const save = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try {
      const res = await api.patch("/ib/local-depositor/profile", form);
      setForm({ ...EMPTY, ...(res.data.profile || {}) });
      toast.success("Payment profile saved");
    } catch (err) { setError(formatApiError(err)); }
    setBusy(false);
  };

  const approve = async (id) => {
    if (approving) return;
    setApproving(id);
    try {
      const res = await api.post(`/ib/local-depositor/requests/${id}/approve`);
      toast.success(`Approved — ${res.data.coins} COINS credited to ${res.data.request?.client_username}`);
      loadWorkspace();
    } catch (err) { toast.error(formatApiError(err)); }
    setApproving(null);
  };

  const bal = data?.balance_coins ?? 0;
  const labelCls = "block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide";

  if (data === undefined) return <div className="max-w-3xl mx-auto py-10 text-sm text-[#64748B]" data-testid="ib-ld-loading">Loading…</div>;
  if (data === null) return (
    <div className="max-w-3xl mx-auto py-10" data-testid="ib-ld-not-active">
      <div className="card-lux p-8 text-center">
        <p className="text-sm text-white font-medium">You are not the active Local Depositor</p>
        <p className="text-xs text-[#64748B] mt-2">This workspace is only available to the currently assigned Local Depositor.</p>
      </div>
    </div>
  );

  const pending = requests.filter((r) => r.status === "pending");
  const history = requests.filter((r) => r.status !== "pending");

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="ib-local-depositor-page">
      <div>
        <p className="eyebrow-label">Local Depositor</p>
        <h1 className="page-title mt-2">Local Depositor Workspace</h1>
        <p className="page-sub mt-1">Review client Buy COINS requests, confirm you received their MYR payment, and approve to credit their COINS.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="card-lux p-5">
          <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B]">Status</p>
          <p className="text-lg font-semibold text-emerald-400 mt-1.5 capitalize" data-testid="ib-ld-status">{data.status}</p>
        </div>
        <div className="card-lux p-5">
          <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B] flex items-center gap-2"><Coins className="h-3.5 w-3.5 text-[#D4AF37]" /> Inventory (read-only)</p>
          <p className="text-2xl font-bold text-[#D4AF37] mt-1 font-mono" data-testid="ib-ld-balance">{bal.toLocaleString()} <span className="text-xs text-[#64748B]">COINS</span></p>
        </div>
      </div>

      {/* Pending buy requests */}
      <div className="card-lux p-6 space-y-4" data-testid="ld-pending-requests">
        <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B] flex items-center gap-2"><Check className="h-3.5 w-3.5 text-[#D4AF37]" /> Pending Buy Requests</p>
        {pending.length === 0 ? (
          <p className="text-sm text-[#64748B]" data-testid="ld-pending-empty">No pending requests right now.</p>
        ) : (
          <div className="space-y-3">
            {pending.map((r) => (
              <div key={r.id} className="rounded-xl border border-[#222530] p-4 space-y-3" data-testid={`ld-request-${r.reference}`}>
                <div className="grid sm:grid-cols-4 gap-3 text-sm">
                  <div><p className="text-[10px] font-mono uppercase text-[#64748B]">Username</p><p className="text-white font-mono">{r.client_username}</p></div>
                  <div><p className="text-[10px] font-mono uppercase text-[#64748B]">UID</p><p className="text-white font-mono">{r.client_uid}</p></div>
                  <div><p className="text-[10px] font-mono uppercase text-[#64748B]">Full Name</p><p className="text-white">{r.client_name}</p></div>
                  <div><p className="text-[10px] font-mono uppercase text-[#64748B]">Reference</p><p className="text-white font-mono">{r.reference}</p></div>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-[#222530]">
                  <div className="flex gap-6 text-sm font-mono">
                    <span className="text-[#94A3B8]">COINS: <span className="text-emerald-400">{r.coins?.toLocaleString()}</span></span>
                    <span className="text-[#94A3B8]">MYR: <span className="text-[#D4AF37]">RM {Number(r.myr || 0).toFixed(2)}</span></span>
                    <span className="text-[#94A3B8] hidden sm:inline">{fmtMYT(r.created_at)}</span>
                  </div>
                  <button
                    onClick={() => approve(r.id)}
                    disabled={approving === r.id}
                    className="btn-gold px-6 py-2.5 text-sm disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
                    data-testid={`ld-approve-${r.reference}`}
                  >
                    <Check className="h-4 w-4" /> {approving === r.id ? "Approving…" : "Approve"}
                  </button>
                </div>
                <p className="text-[10px] text-[#64748B]">Only approve after you have received the client's MYR payment. This credits {r.coins} COINS to the client and deducts your inventory.</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Request history */}
      <div className="card-lux overflow-hidden">
        <p className="px-5 pt-5 text-[10px] font-mono tracking-widest uppercase text-[#64748B]">Approved Requests</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm mt-2" data-testid="ld-request-history">
            <thead><tr className="border-b border-[#222530] text-left">
              <th className="px-5 py-3 text-xs font-medium text-[#64748B]">Date</th>
              <th className="px-5 py-3 text-xs font-medium text-[#64748B]">Client</th>
              <th className="px-5 py-3 text-xs font-medium text-[#64748B]">Reference</th>
              <th className="px-5 py-3 text-xs font-medium text-[#64748B] text-right">COINS</th>
              <th className="px-5 py-3 text-xs font-medium text-[#64748B] text-right">MYR</th>
              <th className="px-5 py-3 text-xs font-medium text-[#64748B]">Status</th>
            </tr></thead>
            <tbody className="divide-y divide-[#222530]">
              {history.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-[#64748B]" data-testid="ld-history-empty">No approved requests yet.</td></tr>
              ) : history.map((r) => (
                <tr key={r.id} data-testid={`ld-history-row-${r.reference}`}>
                  <td className="px-5 py-3 text-[#94A3B8] text-xs font-mono">{fmtMYT(r.approved_at || r.created_at)}</td>
                  <td className="px-5 py-3 text-xs text-white">{r.client_username} <span className="text-[#64748B] font-mono text-[10px]">{r.client_uid}</span></td>
                  <td className="px-5 py-3 text-xs font-mono text-[#94A3B8]">{r.reference}</td>
                  <td className="px-5 py-3 text-right text-xs font-mono text-emerald-400">{r.coins?.toLocaleString()}</td>
                  <td className="px-5 py-3 text-right text-xs font-mono text-[#D4AF37]">RM {Number(r.myr || 0).toFixed(2)}</td>
                  <td className="px-5 py-3 text-xs"><span className={`text-[10px] font-mono uppercase rounded-full px-2 py-0.5 border ${STATUS_STYLES[r.status] || STATUS_STYLES.pending}`}>{r.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment profile */}
      <div className="card-lux p-6">
        <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B] flex items-center gap-2 mb-5"><Landmark className="h-3.5 w-3.5 text-[#D4AF37]" /> Payment Profile</p>
        <form onSubmit={save} className="grid sm:grid-cols-2 gap-5" data-testid="ib-ld-profile-form">
          <div><label className={labelCls}>Bank Name <span className="text-red-400">*</span></label><input className="input-lux" value={form.bank_name} onChange={change("bank_name")} placeholder="e.g. Maybank" required data-testid="ld-bank-name-input" /></div>
          <div><label className={labelCls}>Account Holder Name <span className="text-red-400">*</span></label><input className="input-lux" value={form.account_holder_name} onChange={change("account_holder_name")} placeholder="Full name as per bank" required data-testid="ld-account-holder-input" /></div>
          <div><label className={labelCls}>Bank Account Number <span className="text-red-400">*</span></label><input className="input-lux font-mono" value={form.bank_account_number} onChange={change("bank_account_number")} placeholder="e.g. 0123456789" inputMode="numeric" required data-testid="ld-account-number-input" /></div>
          <div><label className={labelCls}>Link <span className="text-red-400">*</span></label><input className="input-lux font-mono" value={form.link} onChange={change("link")} placeholder="https://..." type="url" required data-testid="ld-link-input" /><p className="mt-1.5 text-[10px] text-[#64748B]">Any valid link (starts with http:// or https://). Clients tap this to contact you.</p></div>
          {error && <p className="sm:col-span-2 text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3" data-testid="ld-profile-error">{error}</p>}
          <div className="sm:col-span-2"><button type="submit" disabled={busy} className="btn-gold px-8 py-3 text-sm disabled:opacity-60 flex items-center gap-2" data-testid="ld-profile-save-button"><Save className="h-4 w-4" /> {busy ? "Saving…" : "Save Payment Profile"}</button></div>
        </form>
      </div>
    </div>
  );
}
